<template>
  <PageContentLayoutOnly headerColor="#fafafa">
    <div>
      <div v-if="formData.uploadOrEnter === 'Enter'" class="pb-3">
        <button
          @click.prevent="finalizeMessageFromState(true)"
          :disabled="hasReadonly || isReadOnly"
          class="button is-primary is-rounded"
        >
          Save
        </button>
      </div>
      <div scheduling class="notification headerColor" v-if="formData">
        <div class="box is-12">
          <ValidationObserver ref="form" v-slot="{ handleSubmit }">
            <transition name="fade" mode="out-in" appear>
              <form @submit.prevent="handleSubmit(onFormSubmit)" class="form">
                <fieldset>
                  <div>
                    <div class="force-center pb-5 subtitle">
                      Is your message saved to a file you need to upload?
                    </div>
                    <div class="columns">
                      <div class="column is-12">
                        <b-field :label="`Upload or Enter Content`">
                          <b-select
                            v-model="formData.uploadOrEnter"
                            required="true"
                            :disabled="hasReadonly || isReadOnly"
                            placeholder="Select Upload or Enter Content"
                          >
                            <option
                              v-for="option in uploadOrEnterList"
                              :value="option.value"
                              :key="option.value"
                            >
                              {{ option.label }}
                            </option>
                          </b-select>
                        </b-field>
                      </div>
                    </div>
                  </div>
                  <div v-if="formData.uploadOrEnter === 'Upload'">
                    <div class="box is-8" v-if="!hasReadonly">
                      <section>
                        <div class="force-center subtitle pb-5">
                          Select File to Upload
                        </div>
                        <div>
                          <!-- Show the Upload Form -->
                          <b-field class="file">
                            <b-upload
                              v-model="file"
                              drag-drop
                              accept="application/pdf"
                              class="rectangle"
                            >
                              <section class="section">
                                <div class="">
                                  <div>
                                    <img
                                      class="Fill-1-Copy-11"
                                      src="@/assets/images/fill-1-copy-11.svg"
                                      alt="Upload"
                                    />
                                  </div>
                                  <div v-if="file">
                                    {{ file.name }}
                                  </div>
                                  <div v-else>
                                    <div class="Drag-Drop-your-fil">
                                      <div class="text-style-1">Drag & Drop</div>
                                      <div>
                                        *Please note:* We only support PDF file uploads at this time
                                        and the file must be less than five pages.
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </section>
                            </b-upload>
                          </b-field>
                        </div>
                        <div class="pt-5">
                          <span class="pr-5"
                            ><b-button
                              type="is-primary"
                              :disabled="!file || isReadOnly || hasReadonly"
                              @click="submitFile"
                              >Upload</b-button
                            ></span
                          >
                        </div>
                      </section>
                    </div>
                    <div>
                      <div class="force-center subtitle pb-5">
                        Preview File
                      </div>
                      <div id="viewpdf" v-if="base64pdf" class="has-text-centered">
                        <div>
                          <div>
                            <div
                              v-dragscroll="true"
                              :class="['doc-viewer', { 'zoom-active': scale > 0.9 }]"
                            >
                              <pdf
                                style="display: none;"
                                :src="base64pdf"
                                @num-pages="pageCount = new Array($event)"
                              />
                              <!-- Actual document -->
                              <pdf
                                v-for="(page, index) in pageCount"
                                :key="index"
                                :src="`${base64pdf}`"
                                :page="index + 1"
                                :scale="scale"
                                :resize="true"
                                :style="{ margin: 'auto', width: `${100 * scale}%` }"
                              />
                            </div>
                          </div>
                          <div class="doc-controls has-text-center">
                            <b-field position="is-centered">
                              <div class="control">
                                <b-button @click.prevent="zoom(-1)">-</b-button>
                              </div>
                              <div class="control">
                                <b-button @click.prevent="zoom(1)">+</b-button>
                              </div>
                            </b-field>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="formData.uploadOrEnter === 'Enter'">
                    <MessageComposer
                      message="*Please note:* Depending on the amount of content added, your communication may span multiple pages."
                      :finalizeMessage="finalizeMessage"
                      :saveMessageForFinalize="saveMessageForFinalize"
                      :showSendTestEmail="false"
                      :showPreview="true"
                      :showAttachments="true"
                      :showMailOptions="true"
                      :processPreview="processFinalPreview"
                      :composedText="mailMessage"
                      :composedIsColor="isColor"
                      :composedIsFullDuplex="isFullDuplex"
                      :handleImageAdded="handleImageAdded"
                      :handleImageRemoved="handleImageRemoved"
                      :isReadOnly="isReadOnly || hasReadonly"
                      :isOnChanged="true"
                    />
                  </div>
                </fieldset>
                <fieldset>
                  <ValidationProvider vid="providerErrors" v-slot="{ errors }">
                    <div class="field">
                      <span>{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </fieldset>
              </form>
            </transition>
          </ValidationObserver>
        </div>
      </div>
    </div>
  </PageContentLayoutOnly>
</template>

<script>
import PageContentLayoutOnly from '@/containers/PageContentLayoutOnly'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapState, mapGetters } from 'vuex'
import MessageComposer from '@/pages/Shared/mailingCommunications/components/communicationModal/keys/MessageComposer'
import { data } from './keys/data'
import { methods } from './keys/methods'
import pdf from 'vue-pdf'
import { dragscroll } from 'vue-dragscroll'

export default {
  name: 'MailCommunication',
  components: {
    ValidationObserver,
    ValidationProvider,
    PageContentLayoutOnly,
    pdf,
    MessageComposer
  },

  directives: { dragscroll },

  data,

  computed: {
    ...mapState({
      hoaId: state => state.user.selectedHoaId,
      ...mapGetters('user', ['authUser']),
      ...mapGetters('communication', [
        'supportMailDelivery',
        'supportEmailDelivery',
        'supportSMSDelivery',
        'isLegalCommunication',
        'mailingTypeID',
        'mailingType',
        'mailMessage',
        'mailingCommunicationID',
        'communicationDescription',
        'selectedDeliveryOwnerIDs',
        'selectedDeliveryOwners',
        'deliveryDate',
        'isFullDuplex',
        'isColor',
        'hasReadonly',
        'compositionMethod'
      ])
    })
  },

  mounted() {
    this.reload()
  },

  watch: {
    hoaId() {
      this.reload()
    },
    'formData.uploadOrEnter'() {
      console.debug('watch uploadOrEnter...')
      this.uploadOrEnterChanged()
    }
  },

  methods,

  i18n: {
    messages: {
      en: {
        mailCommunication: { title: 'Enter Mail Communication' }
      }
    }
  }
}
</script>

<style lang="scss">
#update-work-order .my-grid-class {
  height: 400px;
}

#update-work-order tbody {
  min-height: 100px !important;
  overflow: auto !important;
}
</style>
